/**
 * Wat de drie vlakken van Vandaag met elkaar delen: welke zaak open is,
 * het concept in het antwoordvak, de copiloot en (op de iPhone) welk
 * vlak over het gesprek geschoven is.
 *
 * Waarom een context en geen props door alles heen: de copiloot (rechts)
 * past het concept in het midden aan, en het midden vraagt de copiloot
 * iets (een andere keuze bij Beslissen, vertalen). Die twee kennen elkaar
 * niet; ze praten via deze kleine brug.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { Kanaal, V6UitgaandeBijlage } from "./api";
import { voegBijlagenSamen } from "./bijlagen";
import { filterUitUrl } from "./Lijst/hulp";
import { BEGIN_FILTER, type LijstFilter } from "./lijstQuery";

export type RechtsTab = "klant" | "copiloot";

export interface ConceptVoorstel {
  zaakId: string;
  tekst: string;
  onderwerp: string | null;
  /** Sessie 70 (punt 13): JP vroeg om dit kanaal ("zet dit klaar als
   *  mail"); het antwoordvak wisselt erheen als het aan staat. Null: de
   *  tekst gaat in het kanaal dat open staat. */
  kanaal: Kanaal | null;
  /** Telt op bij elk voorstel, zodat hetzelfde voorstel twee keer mag. */
  volgnummer: number;
}

/**
 * Sessie 55: bijlagen die de copiloot klaarzette (label en pakbon van een
 * retour). Het antwoordvak van die zaak neemt ze over in de mailstand,
 * alsof JP ze zelf koos; dubbele (zelfde id) vallen weg.
 */
export interface BijlagenVoorstel {
  zaakId: string;
  bijlagen: V6UitgaandeBijlage[];
  volgnummer: number;
}

export interface V6Stand {
  /** De geopende zaak, of null als de lijst leeg is. */
  zaakId: string | null;
  /** Open een zaak. Met een kanaal (sessie 72: Nieuw bericht, Mail of
   *  WhatsApp bij de klant) opent het antwoordvak in die stand. */
  kies: (zaakId: string | null, kanaal?: Kanaal | null) => void;
  /** Het kanaal waarin het antwoordvak van deze zaak opent (`?vak=`),
   *  of null: dan kiest het vak zelf. */
  startKanaal: Kanaal | null;

  /** Het nieuwste concept van de copiloot; het midden neemt het over. */
  conceptVoorstel: ConceptVoorstel | null;
  zetConcept: (
    zaakId: string,
    tekst: string,
    onderwerp?: string | null,
    kanaal?: Kanaal | null,
  ) => void;
  /** Het midden heeft het voorstel overgenomen: niet nog eens toepassen. */
  conceptOvergenomen: (volgnummer: number) => void;

  /** Bijlagen van de copiloot die het antwoordvak nog moet overnemen. */
  bijlagenVoorstel: BijlagenVoorstel | null;
  zetBijlagen: (zaakId: string, bijlagen: V6UitgaandeBijlage[]) => void;
  /** Het antwoordvak heeft de bijlagen overgenomen: niet nog eens. */
  bijlagenOvergenomen: (volgnummer: number) => void;
  /** Sessie 55: de bijlagen van een Sanne-concept (`concept_bijlagen`)
   *  komen één keer per concept in het vak. Deze sleutel (zaak plus ids)
   *  is al eens voorgevuld; een nieuwe stand vult niet opnieuw. */
  conceptBijlagenGezien: (sleutel: string) => boolean;
  markeerConceptBijlagen: (sleutel: string) => void;

  /** Het midden meldt hier hoe de tekst in het antwoordvak nu luidt. */
  registreerConceptLezer: (lezer: (() => string) | null) => void;
  leesConcept: () => string;

  /** Stel de copiloot een vraag (van buiten het copilootvlak). */
  vraagCopiloot: (tekst: string) => void;
  /** Het copilootvlak registreert hier wie de vraag afhandelt. */
  registreerCopiloot: (handler: ((tekst: string) => void) | null) => void;

  /** iPhone: welk vlak over het gesprek ligt, of null. */
  rechtsOpen: RechtsTab | null;
  openRechts: (tab: RechtsTab) => void;
  sluitRechts: () => void;

  /** Sessie 53: map, tegel en zoekterm van de lijst nu. Het midden leest
   *  de stand van een kaart uit precies die query. */
  lijstFilter: LijstFilter;
  zetLijstFilter: (filter: LijstFilter) => void;
}

const V6Context = createContext<V6Stand | null>(null);

/** De lijst begint met tegel en map uit de url (sessie 70). */
function beginFilter(): LijstFilter {
  if (typeof window === "undefined") return BEGIN_FILTER;
  return { ...BEGIN_FILTER, ...filterUitUrl(new URLSearchParams(window.location.search)) };
}

export function V6Provider({
  children,
  zaakId,
  kies,
  startKanaal = null,
}: {
  children: ReactNode;
  zaakId: string | null;
  kies: (zaakId: string | null, kanaal?: Kanaal | null) => void;
  startKanaal?: Kanaal | null;
}) {
  const [conceptVoorstel, setConceptVoorstel] = useState<ConceptVoorstel | null>(null);
  const [rechtsOpen, setRechtsOpen] = useState<RechtsTab | null>(null);
  // Sessie 70: tegel en map staan in de url; na F5 begint het midden
  // meteen met de query van de lijst, niet eerst met die van Open.
  const [lijstFilter, setLijstFilter] = useState<LijstFilter>(() => beginFilter());
  const zetLijstFilter = useCallback(
    (f: LijstFilter) =>
      setLijstFilter((oud) =>
        oud.map === f.map && oud.kanaal === f.kanaal && oud.zoek === f.zoek ? oud : f,
      ),
    [],
  );
  const volgnummer = useRef(0);
  const [bijlagenVoorstel, setBijlagenVoorstel] = useState<BijlagenVoorstel | null>(null);
  const conceptBijlagen = useRef<Set<string>>(new Set());
  const conceptLezer = useRef<(() => string) | null>(null);
  const copiloot = useRef<((tekst: string) => void) | null>(null);
  // Een vraag die binnenkomt voordat het copilootvlak er is (iPhone, vlak
  // dicht) wacht hier tot het vlak zich registreert.
  const wachtendeVraag = useRef<string | null>(null);

  const zetConcept = useCallback(
    (id: string, tekst: string, onderwerp: string | null = null, kanaal: Kanaal | null = null) => {
      volgnummer.current += 1;
      setConceptVoorstel({ zaakId: id, tekst, onderwerp, kanaal, volgnummer: volgnummer.current });
    },
    [],
  );

  const conceptOvergenomen = useCallback((nummer: number) => {
    setConceptVoorstel((huidig) => (huidig && huidig.volgnummer === nummer ? null : huidig));
  }, []);

  const zetBijlagen = useCallback((id: string, bijlagen: V6UitgaandeBijlage[]) => {
    if (bijlagen.length === 0) return;
    volgnummer.current += 1;
    const nummer = volgnummer.current;
    // Nog niet overgenomen voor dezelfde zaak: samenvoegen, niets kwijt.
    setBijlagenVoorstel((oud) => ({
      zaakId: id,
      bijlagen: oud && oud.zaakId === id ? voegBijlagenSamen(oud.bijlagen, bijlagen) : bijlagen,
      volgnummer: nummer,
    }));
  }, []);

  const bijlagenOvergenomen = useCallback((nummer: number) => {
    setBijlagenVoorstel((huidig) => (huidig && huidig.volgnummer === nummer ? null : huidig));
  }, []);

  const conceptBijlagenGezien = useCallback(
    (sleutel: string) => conceptBijlagen.current.has(sleutel),
    [],
  );
  const markeerConceptBijlagen = useCallback((sleutel: string) => {
    conceptBijlagen.current.add(sleutel);
  }, []);

  const registreerConceptLezer = useCallback((lezer: (() => string) | null) => {
    conceptLezer.current = lezer;
  }, []);
  const leesConcept = useCallback(() => conceptLezer.current?.() ?? "", []);

  const registreerCopiloot = useCallback((handler: ((tekst: string) => void) | null) => {
    copiloot.current = handler;
    if (handler && wachtendeVraag.current) {
      const vraag = wachtendeVraag.current;
      wachtendeVraag.current = null;
      handler(vraag);
    }
  }, []);

  const vraagCopiloot = useCallback((tekst: string) => {
    if (copiloot.current) copiloot.current(tekst);
    else wachtendeVraag.current = tekst;
  }, []);

  const openRechts = useCallback((tab: RechtsTab) => setRechtsOpen(tab), []);
  const sluitRechts = useCallback(() => setRechtsOpen(null), []);

  const kiesEnReset = useCallback(
    (id: string | null, kanaal: Kanaal | null = null) => {
      setRechtsOpen(null);
      setConceptVoorstel(null);
      setBijlagenVoorstel(null);
      // Een zaak opnieuw openen geeft een vers vak: daar horen de
      // conceptbijlagen weer bij.
      conceptBijlagen.current = new Set();
      wachtendeVraag.current = null;
      kies(id, kanaal);
    },
    [kies],
  );

  const stand = useMemo<V6Stand>(
    () => ({
      zaakId,
      kies: kiesEnReset,
      startKanaal,
      conceptVoorstel,
      zetConcept,
      conceptOvergenomen,
      bijlagenVoorstel,
      zetBijlagen,
      bijlagenOvergenomen,
      conceptBijlagenGezien,
      markeerConceptBijlagen,
      registreerConceptLezer,
      leesConcept,
      vraagCopiloot,
      registreerCopiloot,
      rechtsOpen,
      openRechts,
      sluitRechts,
      lijstFilter,
      zetLijstFilter,
    }),
    [
      zaakId,
      kiesEnReset,
      startKanaal,
      conceptVoorstel,
      zetConcept,
      conceptOvergenomen,
      bijlagenVoorstel,
      zetBijlagen,
      bijlagenOvergenomen,
      conceptBijlagenGezien,
      markeerConceptBijlagen,
      registreerConceptLezer,
      leesConcept,
      vraagCopiloot,
      registreerCopiloot,
      rechtsOpen,
      openRechts,
      sluitRechts,
      lijstFilter,
      zetLijstFilter,
    ],
  );

  return <V6Context.Provider value={stand}>{children}</V6Context.Provider>;
}

/** Als useV6, maar null buiten de provider (losse tests van het vak). */
export function useV6Optioneel(): V6Stand | null {
  return useContext(V6Context);
}

export function useV6(): V6Stand {
  const stand = useContext(V6Context);
  if (!stand) throw new Error("useV6 buiten V6Provider");
  return stand;
}
